import type { EstadoPortaOficina, FerramentaOficinaCorte, IdZonaOperacaoPorta } from '../../dominio/didatico/oficinaCorte'
import type { PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { chaveProgressoIntervencao, resolverInteracaoPorta, type ModoAlargador } from '../../dominio/didatico/interacaoOficina'
import { estadoZonasPorta } from './estadoZonas'
import { MarcadorZona3D } from './MarcadorZona3D'
import { posicaoNoQuadro, quadroDaPorta, ZONAS_DA_CARROCERIA } from './quadroDaPorta'
import { zonasOperacaoDaPorta, type IdPortaOficina } from './zonasOperacaoPorta'

interface Props {
  perfil: PerfilVeiculo
  idPorta: IdPortaOficina
  selecionada: IdZonaOperacaoPorta | null
  /** Estado da porta: é o que faz o marcador registrar o que já foi feito ali. */
  estadoPorta: EstadoPortaOficina
  ferramenta: FerramentaOficinaCorte
  modoAlargador: ModoAlargador
  progressoEstrutural: Record<string, number>
  aoSelecionar: (id: IdZonaOperacaoPorta) => void
  /** Pressionar o marcador já começa a fechar a ferramenta — soltar em qualquer lugar interrompe (ver App.tsx). */
  aoPressionarFerramenta: (id: IdZonaOperacaoPorta) => void
  aoSoltarFerramenta: () => void
  aoPrevisualizar: (id: IdZonaOperacaoPorta) => void
}

/** Marcadores grandes o bastante para clique e toque; a ferramenta encaixa neles. */
export function ZonasOperacaoPorta3D({
  perfil,
  idPorta,
  selecionada,
  estadoPorta,
  ferramenta,
  modoAlargador,
  progressoEstrutural,
  aoSelecionar,
  aoPressionarFerramenta,
  aoSoltarFerramenta,
  aoPrevisualizar,
}: Props) {
  const estados = estadoZonasPorta(estadoPorta)
  // os marcadores acompanham a porta: ela gira na dobradiça e afasta a chapa,
  // e em coordenadas fixas os pontos do extremo traseiro ficavam atrás dela
  const quadro = quadroDaPorta(perfil, idPorta, estadoPorta)

  // sem porta não há onde aplicar ferramenta: os marcadores ficariam
  // flutuando no vazio depois que a peça é retirada
  if (estadoPorta.removida) return null

  return (
    <group>
      {zonasOperacaoDaPorta(perfil, idPorta).map((zona) => {
        const intencao = resolverInteracaoPorta({
          ferramenta,
          zona: zona.id,
          modoAlargador,
          estado: estadoPorta,
          portaDianteira: idPorta.startsWith('portaDianteira'),
        })
        return <MarcadorZona3D
          key={zona.id}
          posicao={ZONAS_DA_CARROCERIA.includes(zona.id)
            ? zona.posicao
            : posicaoNoQuadro(quadro, zona.posicao)}
          // o anel deita no plano da porta, cuja normal é o eixo X — e
          // acompanha a abertura, senão fica torto assim que ela gira
          rotacao={[0, Math.PI / 2 + (ZONAS_DA_CARROCERIA.includes(zona.id) ? 0 : quadro.giroY), 0]}
          nome={zona.nome}
          descricao={intencao.descricao}
          disponivel={intencao.disponivel}
          areaContato={zona.areaContato}
          progressoEstrutural={intencao.disponivel
            ? progressoEstrutural[chaveProgressoIntervencao(intencao.operacao, zona.id)] ?? 0
            : 0}
          estado={estados[zona.id]}
          selecionada={zona.id === selecionada}
          nomeObjeto={`zona-operacao:${zona.id}`}
          aoSelecionar={() => aoSelecionar(zona.id)}
          aoPressionarFerramenta={() => aoPressionarFerramenta(zona.id)}
          aoSoltarFerramenta={aoSoltarFerramenta}
          aoPrevisualizar={() => aoPrevisualizar(zona.id)}
        />
      })}
    </group>
  )
}
